/**
 * Detect gTwins where GeoConverter conversion succeeded but writeback
 * left the scene with zero operator-visible layers (demo-killer).
 *
 * Typical causes (converter-side):
 *   - inferred artifactKind not in ARTIFACT_SCENE_CONTRACT
 *     (e.g. `imagery-basemap` rejected before scene items were written)
 *   - SceneItem field mismatch (`source_crs` vs `sourceCrs`)
 *
 * GeoViewer cannot invent layers, but must surface this loudly instead of
 * silently showing only the injected OSM basemap.
 */

import type { GTwinRecord, SceneItem } from '@/sdk-stub/gtwin-sdk';

export interface ConverterWritebackGap {
  readonly detected: boolean;
  readonly runId: string | null;
  readonly artifactCount: number;
  readonly sceneLayerCount: number | null;
  readonly warning: string | null;
  /** Short operator-facing title. */
  readonly title: string;
  /** Short operator-facing body. */
  readonly body: string;
}

function slotItemCount(scene: GTwinRecord['payload']['geospatial']['scene'] | undefined): number {
  if (!scene) return 0;
  const terrain = scene.terrain ? 1 : 0;
  const arrays = [
    scene.basemaps,
    scene.realityData,
    scene.featureLayers,
    scene.engineeringModels,
    scene.overlays,
    scene.environment,
  ];
  return terrain + arrays.reduce((sum, list) => sum + (list?.length ?? 0), 0);
}

function firstConversion(record: GTwinRecord): Record<string, unknown> | null {
  const conversions = (record.payload?.geospatial as { conversions?: unknown } | undefined)
    ?.conversions;
  if (!Array.isArray(conversions) || conversions.length === 0) return null;
  const latest = conversions[conversions.length - 1];
  return latest && typeof latest === 'object'
    ? (latest as Record<string, unknown>)
    : null;
}

function readWarning(record: GTwinRecord, conversion: Record<string, unknown> | null): string | null {
  const scene = record.payload?.geospatial?.scene as
    | { _converterRefs?: { readinessReportRef?: unknown } }
    | undefined;
  const packageWarnings = (
    record as unknown as {
      payload?: {
        geospatial?: {
          scene?: { readinessSummary?: { warnings?: unknown } };
        };
      };
    }
  ).payload?.geospatial?.scene;

  // Prefer conversion / package readiness warnings when present on the record.
  const fromScene = (scene as { readinessSummary?: { warnings?: unknown } } | undefined)
    ?.readinessSummary?.warnings;
  if (Array.isArray(fromScene)) {
    const hit = fromScene.find((w) => typeof w === 'string' && /ARTIFACT_SCENE_CONTRACT|writeback|inferred as/i.test(w));
    if (typeof hit === 'string') return hit;
  }

  void packageWarnings;
  void conversion;
  return null;
}

/**
 * True when the twin has conversion provenance / artifacts but no inline
 * scene items the viewer can render (other than what we inject later).
 */
export function detectConverterWritebackGap(record: GTwinRecord): ConverterWritebackGap {
  const scene = record.payload?.geospatial?.scene;
  const inlineCount = slotItemCount(scene);
  const conversion = firstConversion(record);
  const artifactCount =
    typeof conversion?.artifactCount === 'number' ? conversion.artifactCount : 0;
  const sceneLayerCount =
    typeof conversion?.sceneLayerCount === 'number' ? conversion.sceneLayerCount : null;
  const runId = typeof conversion?.runId === 'string' ? conversion.runId : null;
  const hasConverterRefs = Boolean(
    (scene as { _converterRefs?: unknown } | undefined)?._converterRefs
    || conversion,
  );

  const emptyInline = inlineCount === 0;
  const convertedButEmpty =
    emptyInline
    && hasConverterRefs
    && (artifactCount > 0 || sceneLayerCount === 0 || sceneLayerCount === null);

  if (!convertedButEmpty) {
    return {
      detected: false,
      runId,
      artifactCount,
      sceneLayerCount,
      warning: null,
      title: '',
      body: '',
    };
  }

  const warning = readWarning(record, conversion);
  return {
    detected: true,
    runId,
    artifactCount,
    sceneLayerCount,
    warning,
    title: 'Converted file did not write into this project',
    body:
      warning
      ?? (runId
        ? `GeoConverter run ${runId} produced artifact(s) but wrote 0 scene layers (writeback failed). The map basemap alone is not your data — fix writeback in GeoConverter, then re-open or retry writeback.`
        : 'GeoConverter produced artifact(s) but wrote 0 scene layers (writeback failed). The map basemap alone is not your data — fix writeback in GeoConverter, then re-open or retry writeback.'),
  };
}

/** Count non-synthetic layers already in the catalog (excludes injected OSM). */
export function countRenderableSceneItems(items: readonly SceneItem[]): number {
  return items.length;
}
